var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import SendBird from 'sendbird';
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { getInboxItemChannel, sortInboxItems } from './utils';
const INBOX_QUERY_KEY = 'inbox';
export function useConnectionStatus({ userId }) {
    const [connectionStatus, setConnectionStatus] = useState(SendBird.getInstance().getConnectionState());
    useEffect(() => {
        const sb = SendBird.getInstance();
        const connectionHandler = new sb.ConnectionHandler();
        const updateConnectionStatus = () => {
            setConnectionStatus(sb.getConnectionState());
        };
        connectionHandler.onReconnectFailed = updateConnectionStatus;
        connectionHandler.onReconnectStarted = updateConnectionStatus;
        connectionHandler.onReconnectSucceeded = updateConnectionStatus;
        const connectionHandlerId = 'inboxConnectionHandler';
        sb.addConnectionHandler(connectionHandlerId, connectionHandler);
        return () => {
            sb.removeConnectionHandler(connectionHandlerId);
        };
    }, []);
    return useMemo(() => {
        return { isConnected: connectionStatus === 'OPEN', connectionStatus };
    }, [connectionStatus]);
}
function createChannelListQuery() {
    const listQuery = SendBird.getInstance().GroupChannel.createMyGroupChannelListQuery();
    listQuery.includeEmpty = true;
    listQuery.memberStateFilter = 'joined_only';
    listQuery.order = 'latest_last_message';
    listQuery.limit = 100;
    return listQuery;
}
const replaceInboxItemChannel = (item, channel) => {
    return Object.assign(Object.assign({}, item), { channel });
};
export function useInbox({ appId, userId, shouldIncludeRecentMessages, enabled = true, }) {
    const { isConnected } = useConnectionStatus({ userId });
    const { data = [], isLoading, refetch, error, } = useQuery(INBOX_QUERY_KEY, function getInboxItems() {
        const promise = new Promise((resolve, reject) => {
            const listQuery = createChannelListQuery();
            listQuery.next((channels, error) => __awaiter(this, void 0, void 0, function* () {
                if (error) {
                    reject(error);
                    return;
                }
                const recentMessages = shouldIncludeRecentMessages &&
                    (yield Promise.all(channels.filter(shouldIncludeRecentMessages).map((channel) => new Promise((resolve) => {
                        const listQuery = channel.createPreviousMessageListQuery();
                        listQuery.limit = 3;
                        listQuery.messageTypeFilter = 'MESG';
                        listQuery.reverse = true;
                        listQuery.load((messages, originalError) => {
                            if (originalError) {
                                const error = new Error('Fetching recent messages failed.', 
                                // @ts-ignore
                                { cause: originalError });
                                reject(error);
                                return;
                            }
                            resolve([channel.url, messages]);
                        });
                    }))).then((recentMessages) => Object.fromEntries(recentMessages)));
                const inboxItems = channels.map((channel) => ({
                    inboxItemType: 'channel',
                    channel,
                    recentMessages: recentMessages === null || recentMessages === void 0 ? void 0 : recentMessages[channel.url],
                }));
                resolve(sortInboxItems(inboxItems));
            }));
        });
        return promise;
    }, { enabled: isConnected && enabled });
    const queryClient = useQueryClient();
    useEffect(() => {
        const sb = SendBird.getInstance();
        const channelHandler = new sb.ChannelHandler();
        function removeChannel(deletedChannelUrl) {
            queryClient.setQueryData(INBOX_QUERY_KEY, (data) => {
                if (!data) {
                    return [];
                }
                return data.filter((item) => {
                    const channel = getInboxItemChannel(item);
                    return channel.url !== deletedChannelUrl;
                });
            });
        }
        channelHandler.onUserJoined = (channel, user) => {
            if (user.userId === sb.currentUser.userId && enabled) {
                refetch();
            }
        };
        channelHandler.onUserLeft = (channel, user) => {
            if (user.userId === sb.currentUser.userId) {
                removeChannel(channel.url);
            }
        };
        channelHandler.onChannelChanged = (changedChannel) => {
            if (!changedChannel.isGroupChannel()) {
                return;
            }
            queryClient.setQueryData(INBOX_QUERY_KEY, (data) => {
                if (!data) {
                    return [];
                }
                return sortInboxItems(data.map((item) => {
                    const channel = getInboxItemChannel(item);
                    return channel.url === changedChannel.url
                        ? replaceInboxItemChannel(item, changedChannel)
                        : item;
                }));
            });
        };
        channelHandler.onChannelDeleted = (deletedChannelUrl) => {
            removeChannel(deletedChannelUrl);
        };
        sb.addChannelHandler('inboxChannelHandler', channelHandler);
        return () => {
            sb.removeChannelHandler('inboxChannelHandler');
        };
    }, [queryClient, refetch]);
    return useMemo(() => ({ data, isLoading: !isConnected || isLoading, refetch, error }), [data, isLoading, refetch, error]);
}
